import { Fragment } from "react";
import { DevTool } from "@hookform/devtools";
import { FixedSizeList as VirtualizedList } from "react-window";
import { RetryFailed, TrashCan } from "@carbon/icons-react";
import { Link } from "react-router-dom";

import {
  CurrencyDisplay,
  FileUpload,
  ModalContainer,
  PinCodeInputModal,
  SearchBar,
  SectionHeader,
  SectionSubheader,
  SwitchAppButton,
  SwitchAppContentSwitcher,
  Toggle,
} from "../../../../components";
import { HookFormSelect, TextInput } from "../../../../hook-form";
import { formatAsCurrency, getCurrencySymbol } from "../../../../utils";
import {
  ICreateBatchBankTransferFormViewProps,
  TBatchExcelOrSingleTransfers,
} from "./CreateBatchBankTransferForm";
import { SingleBankTransferForm } from "./SingleBankTransferForm/SingleBankTransferForm";
import { SingleGatewayTransferForm } from "./SingleGatewayTransferForm/SingleGatewayTransferForm";
import {
  BatchBankTransferEntryStructuredListItem,
  BatchGatewayTransferEntryStructuredListItem,
  BatchBankTransferStructuredListTableHeader,
  BatchGatewayTransferStructuredListTableHeader,
} from "./BatchTransferListItem";
import { BankCodesForBulkUpload } from "./BankCodesForBulkUpload";
import {
  ICreateBankTransfer,
  ICreateGatewayTransfer,
} from "../../../../interfaces-and-types";

const searchBankRecipient = (
  transfer: ICreateBankTransfer,
  searchRecipientsText: string | null
) =>
  !searchRecipientsText ||
  transfer.amount.toString().includes(searchRecipientsText) ||
  transfer.accountNameHelper
    ?.toLowerCase()
    .includes(searchRecipientsText.toLowerCase()) ||
  transfer.account_number?.includes(searchRecipientsText) ||
  transfer.bankNameHelper
    ?.toLowerCase()
    .includes(searchRecipientsText.toLowerCase()) ||
  transfer.reference
    ?.toLowerCase()
    .includes(searchRecipientsText.toLowerCase());

const searchGatewayRecipient = (
  transfer: ICreateGatewayTransfer,
  searchRecipientsText: string | null
) =>
  !searchRecipientsText ||
  transfer.amount.toString().includes(searchRecipientsText) ||
  transfer.merchantNameHelper
    ?.toLowerCase()
    .includes(searchRecipientsText.toLowerCase()) ||
  transfer.merchant_id_or_code?.includes(searchRecipientsText) ||
  transfer.gatewayNameHelper
    ?.toLowerCase()
    .includes(searchRecipientsText.toLowerCase()) ||
  transfer.reference
    ?.toLowerCase()
    .includes(searchRecipientsText.toLowerCase());

export const View = ({
  isModalOpen,
  closeModal,
  isLoadingCreateBatchBankTransfer,
  isLoadingBatchListVerification,

  showAllOptions,
  toggleShowAllOptions,
  isOpenBankCodesForBulkUpload,
  toggleShowBankCodesForBulkUpload,
  entriesWithModifiedReferences,

  batchExcelOrSingleTransfers,
  setBatchExcelOrSingleTransfers,
  handleUploadCsvBatch,
  clearBatchList,
  verifyBatchUploadListAndCreateBeneficiaries: reVerifyTransfers,
  searchRecipientsText,
  setSearchRecipientsText,

  bankTransfersFieldArray,
  removeBankTransferByIndex,
  appendNewBankTransfer,
  updateBankTransferByIndex,
  toggleBankTransferBatchEntryEditMode,

  gatewayTransfersFieldArray,
  removeGatewayTransferByIndex,
  appendNewGatewayTransfer,
  updateGatewayTransferByIndex,
  toggleGatewayTransferBatchEntryEditMode,

  isOpenShowPinInput,
  onOpenShowPinInput,
  onCloseShowPinInput,
  combinedVerifyPinInputErrors,
  onVerifyPinSubmit,
  onVerifyPinCorrect,

  isLoadingBalances,
  balances,

  formValues,
  control,
  errors,
  handleSubmit,
  register,
}: ICreateBatchBankTransferFormViewProps) => {
  const isBulkExcelUploadMode =
    batchExcelOrSingleTransfers === "bankExcelUpload";
  const isGatewayTransferMode =
    batchExcelOrSingleTransfers === "singleGatewayTransfers";
  const containsTransfersWithFailedVerification =
    formValues.transfers.find(
      (trsf) => !trsf.isBeneficiaryResolved && trsf.isErrorResolveBeneficiary
    ) !== undefined;
  const indexToEdit = (
    isGatewayTransferMode ? formValues.gateway_transfers : formValues.transfers
  ).findIndex((trsf) => trsf.isEditModeHelper);

  const bankTransfersCount = formValues.transfers.length;
  const gatewayTransfersCount = formValues.gateway_transfers.length;

  const totalBankAndGatewayTransfersCount =
    bankTransfersCount + gatewayTransfersCount;
  const totalBankAndGatewayTransfersAmount =
    bankTransfersFieldArray.reduce(
      (bnkAccum, entry) => bnkAccum + entry.amount,
      0
    ) +
    gatewayTransfersFieldArray.reduce(
      (gwAcc, entry) => gwAcc + entry.amount,
      0
    );

  return (
    <>
      <ModalContainer
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        fullscreen
        modalHeader={
          <SectionHeader
            title="Transfers"
            subtitle="Transfer funds from your SwitchApp account to a bank or gateway."
            renderRight={
              <div className="flex-container justify-content--end mt-5">
                <div className="flex-container">
                  <SwitchAppButton
                    kind="secondary"
                    onClick={closeModal}
                    size="sm"
                    className="d-none d-md-inherit"
                  >
                    Cancel
                  </SwitchAppButton>

                  <SwitchAppButton
                    kind="primary"
                    size="sm"
                    className="ml-5"
                    disabled={
                      totalBankAndGatewayTransfersCount === 0 ||
                      containsTransfersWithFailedVerification
                    }
                    isLoading={isLoadingCreateBatchBankTransfer}
                    onClick={onOpenShowPinInput}
                    title={
                      totalBankAndGatewayTransfersCount === 0
                        ? "Add transfers to the batch"
                        : containsTransfersWithFailedVerification
                        ? "Some recipients in the list could not be verified"
                        : isLoadingCreateBatchBankTransfer
                        ? "Sending request..."
                        : "Send transfer request"
                    }
                  >
                    Initiate Transfers
                  </SwitchAppButton>
                </div>
              </div>
            }
          />
        }
      >
        <div className="flex-container justify-content--center flex-break--small mb-5">
          {/* <div className="flex-box alignment-helper" /> */}
          <div className="flex-box">
            <SwitchAppContentSwitcher<TBatchExcelOrSingleTransfers>
              onChange={({ name }) => {
                setBatchExcelOrSingleTransfers(name);
              }}
              options={[
                {
                  key: "Bank",
                  value: "singleBankTransfers",
                },
                {
                  key: "SwitchApp", // "Gateway",
                  value: "singleGatewayTransfers",
                },
                {
                  key: "Bulk Upload (Excel)",
                  value: "bankExcelUpload",
                },
              ]}
              selectedValue={batchExcelOrSingleTransfers}
            />
          </div>
          <div className="flex-box flex-1_5 alignment-helper" />
        </div>

        <div className="create-batch-transfer-container flex-container row flex-break--small">
          {isBulkExcelUploadMode ? null : (
            <>
              <div className="flex-box mr-md-5">
                {isGatewayTransferMode ? (
                  <SingleGatewayTransferForm
                    appendSingleGatewayTransfer={appendNewGatewayTransfer}
                    gatewayTransfersList={formValues.gateway_transfers}
                  />
                ) : (
                  <SingleBankTransferForm
                    sourceCurrency={formValues.source_currency}
                    appendSingleBankTransfer={appendNewBankTransfer}
                    bankTransfersList={formValues.transfers}
                  />
                )}
              </div>

              <div className="flex-divider--vertical" />
            </>
          )}

          <div className="flex-box flex-1_5 flex-container col">
            {/* <form onSubmit={onOpenShowPinInput}> */}
            <form onSubmit={() => {}}>
              <div className="flex-container justify-content--between">
                <SectionSubheader
                  title="BATCH DETAILS"
                  // className=""
                />

                <Toggle
                  name="showAllOptions"
                  title={"Show All Options"}
                  labelOff={
                    <>
                      <span
                        title="Click to display all options available for configuring the batch transfer"
                        className="account-mode--test font-ctm--bold"
                      >
                        Show All Options
                      </span>
                    </>
                  }
                  labelOn={
                    <>
                      <span
                        title="Click to hide all options available for configuring the batch transfer"
                        className="account-mode--live font-ctm--bold"
                      >
                        Show All Options
                      </span>
                    </>
                  }
                  size="sm"
                  toggled={showAllOptions}
                  onToggle={toggleShowAllOptions}
                />
              </div>

              {/* Causes pushing new page to prompt if form is dirty */}
              {/* {!isDirty ? null : 
                      <PromptOnDirtyFormClose
                        when={globalStore.isLoadingMainScreen}
                        message={
                          () => `Are you sure you want to leave this page?`
                          // location => `Are you sure you want to go to ${location.pathname}`
                        }
                      />}
                  */}

              <div className="flex-container flex-break--small">
                <div className="flex-box">
                  <TextInput
                    name="title"
                    label="Batch title (Optional)"
                    placeholder="e.g. May 2022 Salaries"
                    infoIconText="Title for the batch of transfers in the list eg May 2022 Salaries"
                    type="text"
                    register={register}
                    errors={errors}
                  />
                </div>
                {/* <div className="flex-box mr-md-5">xxxxxxxxxxxx</div> */}
              </div>

              {!showAllOptions ? null : (
                <>
                  <div className="flex-container flex-break--small align-items--start">
                    <div className="flex-box mr-md-5">
                      <HookFormSelect
                        name="source_currency"
                        label="Source Wallet/Currency"
                        infoIconText="Your account balance to make transfer from eg NGN - Account, USD - Account. If the amount is not in the transfer currency, we will convert it. "
                        showSkeleton={isLoadingBalances}
                        selectItems={balances.map((bal) => ({
                          text: `${bal.currency} - BALANCE: ${getCurrencySymbol(
                            bal.currency
                          )}${formatAsCurrency(bal.available_wallet_balance)})`,
                          value: bal.currency,
                        }))}
                        register={register}
                        errors={errors}
                      />
                    </div>

                    <div className="flex-box">
                      <TextInput
                        name="batch_reference"
                        label="Batch Reference"
                        infoIconText="Batch reference for this transfer"
                        type="text"
                        register={register}
                        errors={errors}
                      />
                    </div>
                  </div>

                  <TextInput
                    name="description"
                    label="Batch description (Optional)"
                    placeholder="e.g. Performance bonuses were included in the salaries this month"
                    infoIconText="A general description for the transfers in this batch (Optional)"
                    type="text"
                    register={register}
                    errors={errors}
                  />
                </>
              )}
              <span className="text-danger font-ctm--semibold fontSize-12">
                {combinedVerifyPinInputErrors}
              </span>

              <div className="mt-5">
                <div className="batch-list--title flex-container align-items--start">
                  <div className="flex-box">
                    <h4 className="mb-5">
                      <strong>
                        Total ({totalBankAndGatewayTransfersCount}):{" "}
                        <CurrencyDisplay
                          amount={totalBankAndGatewayTransfersAmount}
                          currencyOrSymbol={formValues.source_currency}
                        />
                        {/* </span> */}
                      </strong>
                    </h4>
                  </div>

                  <div className="flex-container align-items--start">
                    {!isBulkExcelUploadMode ? null : (
                      <>
                        {bankTransfersCount > 0 ? null : (
                          <Link
                            to="/templates/switchapp-bulk-transfer - TEMPLATE.csv"
                            download="switchapp-bulk-transfer - TEMPLATE.csv"
                            target="_blank"
                            className="csv-template-download--link font-ctm--bold mr-5"
                          >
                            <span className="text-dark">Download </span>
                            TEMPLATE
                            <span className="text-dark"> here</span>
                          </Link>
                        )}

                        <div
                          className="bank-codes--link mr-5"
                          onClick={toggleShowBankCodesForBulkUpload}
                        >
                          View
                          <span className="text-success font-ctm--bold">
                            {" "}
                            BANK{" "}
                          </span>
                          Codes
                        </div>
                      </>
                    )}

                    {!containsTransfersWithFailedVerification ? null : (
                      <SwitchAppButton
                        size="sm"
                        kind="tertiary"
                        onClick={reVerifyTransfers}
                        className="align-items--center mr-5"
                      >
                        <RetryFailed className="mr-3 text-success" />
                        Re-run All Recipient Verification
                      </SwitchAppButton>
                    )}

                    {!isBulkExcelUploadMode ? null : (
                      <FileUpload
                        name={"uploadExcelBatchTransfers"}
                        accept={[".csv"]}
                        // kind="success"
                        // icon={<ArrowUp />}
                        // arrayFieldName={xxxxxx}
                        // arrayFieldIndex={xxxxxx}
                        buttonLabel="Upload Batch Document (.csv)"
                        className={""}
                        containerClassName={""}
                        // showSkeleton={isLoadingExcelHandler}
                        // invalid={excelContainsInvalidEntries}
                        // invalidText={excelInvalidRowsWithDescription}
                        // value={excelFileName}
                        onChange={handleUploadCsvBatch}
                      />
                    )}

                    {totalBankAndGatewayTransfersCount < 2 ? null : (
                      <SwitchAppButton
                        size="sm"
                        kind="danger"
                        onClick={clearBatchList}
                        className="align-items--center ml-5"
                      >
                        <TrashCan className="mr-3" />
                        Clear All
                      </SwitchAppButton>
                    )}
                  </div>
                </div>

                {totalBankAndGatewayTransfersCount < 5 ? null : (
                  <SearchBar
                    name="search-recipients"
                    placeholder="Search recipients in batch..."
                    onChange={(e: any) =>
                      setSearchRecipientsText(e.target.value)
                    }
                    onClear={() => setSearchRecipientsText("")}
                    className="recipients-searchbar"
                  />
                )}

                <div className="cds--structured-list">
                  {/* NOTE: moved to above first element to handle layout issues */}
                  {/* <BatchTransferStructuredListTableHeader /> */}

                  <VirtualizedList
                    itemCount={bankTransfersCount}
                    itemSize={50}
                    height={
                      bankTransfersCount
                        ? 50 * bankTransfersCount // 800
                        : 0
                    }
                    width={"100%"}
                    className="cds--structured-list-tbody"
                  >
                    {({ index: virtualizedRowIndex, style }) => {
                      const bnkTransfer =
                        bankTransfersFieldArray[virtualizedRowIndex];

                      const isSearchFilterValid = searchBankRecipient(
                        bnkTransfer,
                        searchRecipientsText
                      );

                      if (!isSearchFilterValid) return null;

                      return (
                        <Fragment
                          key={`${virtualizedRowIndex}-${bnkTransfer.reference}`}
                        >
                          {virtualizedRowIndex === 0 ? (
                            <BatchBankTransferStructuredListTableHeader />
                          ) : null}

                          <BatchBankTransferEntryStructuredListItem
                            key={`${virtualizedRowIndex}-${bnkTransfer.reference}`}
                            transfer={bnkTransfer}
                            index={virtualizedRowIndex}
                            isLoadingBatchListVerification={
                              isLoadingBatchListVerification
                            }
                            batchListCount={totalBankAndGatewayTransfersCount}
                            removeBankTransferByIndex={
                              removeBankTransferByIndex
                            }
                            toggleBankTransferBatchEntryEditMode={
                              toggleBankTransferBatchEntryEditMode
                            }
                            hasModifiedReference={
                              entriesWithModifiedReferences.find(
                                (index) => index === virtualizedRowIndex
                              ) !== undefined
                            }
                            reVerifyTransfers={reVerifyTransfers}
                            virtualizedListStyle={style}
                          />
                        </Fragment>
                      );
                    }}
                  </VirtualizedList>
                </div>

                {gatewayTransfersFieldArray.length === 0 ? null : (
                  <div className="cds--structured-list">
                    <VirtualizedList
                      itemCount={gatewayTransfersCount}
                      itemSize={50}
                      height={
                        gatewayTransfersCount
                          ? 50 * gatewayTransfersCount // 800
                          : 0
                      }
                      width={"100%"}
                      className="cds--structured-list-tbody"
                    >
                      {({ index: virtualizedRowIndex, style }) => {
                        const gwTransfer =
                          gatewayTransfersFieldArray[virtualizedRowIndex];

                        const isSearchFilterValid = searchGatewayRecipient(
                          gwTransfer,
                          searchRecipientsText
                        );

                        if (!isSearchFilterValid) return null;

                        return (
                          <Fragment
                            key={`${virtualizedRowIndex}-${gwTransfer.reference}`}
                          >
                            {virtualizedRowIndex === 0 ? (
                              <BatchGatewayTransferStructuredListTableHeader />
                            ) : null}

                            <BatchGatewayTransferEntryStructuredListItem
                              key={`${virtualizedRowIndex}-${gwTransfer.reference}`}
                              transfer={gwTransfer}
                              index={virtualizedRowIndex}
                              isLoadingBatchListVerification={
                                isLoadingBatchListVerification
                              }
                              batchListCount={totalBankAndGatewayTransfersCount}
                              removeGatewayTransferByIndex={
                                removeGatewayTransferByIndex
                              }
                              toggleGatewayTransferBatchEntryEditMode={
                                toggleGatewayTransferBatchEntryEditMode
                              }
                              reVerifyTransfers={reVerifyTransfers}
                              virtualizedListStyle={style}
                            />
                          </Fragment>
                        );
                      }}
                    </VirtualizedList>
                  </div>
                )}

                {totalBankAndGatewayTransfersCount > 0 ? null : (
                  <div className="empty-transfers--list flex-container justify-content--center align-items--center">
                    <div className="">No recipients in the batch...</div>
                  </div>
                )}
              </div>
              {/* set up the dev tool */}
              <DevTool control={control} />
            </form>
          </div>
        </div>
      </ModalContainer>

      {!isOpenShowPinInput ? null : (
        <PinCodeInputModal
          title="Enter Transfer PIN"
          subtitle="Enter your 4-digit code for transfers"
          // buttonText="Submit PIN"
          errorMessage={combinedVerifyPinInputErrors} // get from useApiErrors()
          showPinValues={false}
          isModalOpen={isOpenShowPinInput}
          closeModal={onCloseShowPinInput}
          onChange={onVerifyPinSubmit}
          handlePinSubmit={onVerifyPinCorrect}
          pin={formValues.pin}
          pinCodeLength={4}
          // onPinCorrect={onVerifyPinCorrect}
        />
      )}

      {indexToEdit === -1 || isGatewayTransferMode ? null : (
        <div>
          <ModalContainer
            isModalOpen={indexToEdit > -1}
            closeModal={() => toggleBankTransferBatchEntryEditMode(indexToEdit)}
            className="edit-entry-modal-container"
          >
            <SingleBankTransferForm
              sourceCurrency={formValues.source_currency}
              appendSingleBankTransfer={(updatedTransfer) => {
                updateBankTransferByIndex(indexToEdit, updatedTransfer);
              }}
              bankTransfersList={formValues.transfers}
              initialBatchEntryValues={formValues.transfers[indexToEdit]}
              isBatchEditMode
            />

            <div className="flex-container row justify-content--center mt-5 mb-5" />
          </ModalContainer>
        </div>
      )}

      {indexToEdit === -1 || !isGatewayTransferMode ? null : (
        <div>
          <ModalContainer
            isModalOpen={indexToEdit > -1}
            closeModal={() =>
              toggleGatewayTransferBatchEntryEditMode(indexToEdit)
            }
            className="edit-entry-modal-container"
          >
            <SingleGatewayTransferForm
              appendSingleGatewayTransfer={(updatedTransfer) => {
                updateGatewayTransferByIndex(indexToEdit, updatedTransfer);
              }}
              gatewayTransfersList={formValues.gateway_transfers}
              initialBatchEntryValues={
                formValues.gateway_transfers[indexToEdit]
              }
              isBatchEditMode
            />

            <div className="flex-container row justify-content--center mt-5 mb-5" />
          </ModalContainer>
        </div>
      )}

      {!isOpenBankCodesForBulkUpload ? null : (
        <BankCodesForBulkUpload
          isModalOpen={isOpenBankCodesForBulkUpload}
          closeModal={toggleShowBankCodesForBulkUpload}
        />
      )}
    </>
  );
};
